import type {
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudRaisersEdgeIntegrationAttributes,
} from '@feathr/blackbox';
import type {
  IIntegrationUIToggleSection,
  TIntegrationUISettingField,
  TIntegrationUIToggleValues,
} from '@feathr/components';

import {
  CONSTITUENT_DOWNSYNC_KEY,
  DONATION_UPSYNC_KEY,
  GIFT_DOWNSYNC_KEY,
} from '../raisersEdgeConfig';

type TDownsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['downsync_activities'];
type TUpsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['upsync_activities'];

/** The sync toggles' values from the model. */
export function getActivityValues({
  downsync_activities: downsync,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'downsync_activities' | 'upsync_activities'>
>): TIntegrationUIToggleValues {
  return {
    [CONSTITUENT_DOWNSYNC_KEY]: downsync?.constituents ?? false,
    [DONATION_UPSYNC_KEY]: upsync?.donations ?? false,
    [GIFT_DOWNSYNC_KEY]: downsync?.gift_events ?? false,
  };
}

/** The downsync activities with one activity set. Keeps the other activities as they are. */
export function setDownsyncActivity(
  downsync: TDownsyncActivities | undefined,
  key: keyof TDownsyncActivities,
  value: boolean,
): TDownsyncActivities {
  return { constituents: false, gift_events: false, ...downsync, [key]: value };
}

/** Stages one downsync activity on the model. The page's Save & Sync persists it. */
export function stageDownsyncActivity(
  integration: BlackbaudRaisersEdgeIntegration,
  key: keyof TDownsyncActivities,
  value: boolean,
): void {
  integration.set({
    downsync_activities: setDownsyncActivity(integration.get('downsync_activities'), key, value),
  });
}

/** The upsync activities with donation upsync set. Keeps the other activities as they are. */
export function setDonationUpsync(
  upsync: TUpsyncActivities | undefined,
  value: boolean,
): TUpsyncActivities {
  return { ...upsync, donations: value };
}

/** Whether donation upsync is on with no fund to put the donations in. */
export function isFundMissing({
  fundraising,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'fundraising' | 'upsync_activities'>
>): boolean {
  return !!upsync?.donations && !fundraising?.fund_id;
}

/** The field a toggle shows while it is on, if the sections declare one. */
export function getToggleField(
  sections: readonly IIntegrationUIToggleSection[],
  key: string,
): TIntegrationUISettingField | undefined {
  return sections.flatMap((section) => section.toggles).find((toggle) => toggle.key === key)?.field;
}

/** The sections with one toggle disabled for `reason`. */
export function disableToggle(
  sections: readonly IIntegrationUIToggleSection[],
  key: string,
  reason: string,
): IIntegrationUIToggleSection[] {
  return sections.map((section) => ({
    ...section,
    toggles: section.toggles.map((toggle) =>
      toggle.key === key ? { ...toggle, disabledReason: reason } : toggle,
    ),
  }));
}
